import moment, { unitOfTime } from 'moment';
import ENDPOINTS from '../constants/apiEndPoints';
import routePermissions from '../constants/routePermissions';
import { getToken } from 'firebase/messaging';
import { messaging } from '../firebase/firebaseConfig';
import { ADDITIONAL_COLORS } from '../pages/users/data';
import { SVG } from '../constants/assetLists';
import { FilterItem } from '../pages/operations/components/AnnouncementsAndTasks/components/FilterSidebar';
import { clearReactQueryCache } from './queryClientRegistry';
import { FormQuestion } from '../services/operations/forms';
import { UserItem } from '../pages/users/components/UserScreens/users';
import {
  DepartmentFormValues,
  NetworkNodeFormValues,
  RoleFormValues,
} from '../pages/on-boarding/components/NetworkDetails/types';
import { ClientOption } from '../pages/on-boarding/components/NetworkDetails/components/tab-screens/users/components/NotifyDeptTreeSelect';

type RenewalRow = { renewalDate: string; reminderBeforeDays: string };

export const hasPermission = (permissionName: string) => {
  const permissions = getUserPermissions();

  return isSuperAdmin() || permissions.includes(permissionName);
};

export const hasAnyPermission = (permissionNames: string[]) => {
  const permissions = getUserPermissions();

  const hasPermission = permissionNames.some((p) => permissions.includes(p));

  return isSuperAdmin() || hasPermission;
};

export const getUserPermissions = () => {
  const stringifyUser = localStorage.getItem('user');
  const user =
    stringifyUser && stringifyUser !== 'undefined'
      ? JSON.parse(stringifyUser)
      : null;
  const userRoles = user?.roles || [];

  const rolePermissions = userRoles.reduce(
    (acc: string[], role: Record<string, any>) => {
      return [
        ...acc,
        ...(role?.permissions?.map((p: Record<string, any>) => p.name) || []),
      ];
    },
    [],
  );

  const individualPermissions = (user?.permissions || []).map(
    (p: Record<string, any>) => p.name,
  );

  return individualPermissions?.length > 0
    ? [...individualPermissions]
    : [...rolePermissions];
};

export const findFirstAccessibleRoute = () => {
  const permissions = getUserPermissions(); // returns array of strings

  if (isSuperAdmin(true) && !isNetworkOwnerOnBoardingCompleted()) {
    return '/on-boarding';
  }

  if (isSuperAdmin()) {
    return '/operations';
  }

  for (const route of routePermissions) {
    const hasPermission = permissions.some(
      (p: any) => route.permissions.includes(p) && !route.disabled,
    );
    if (hasPermission) return route.path;
  }

  localStorage.clear();
  return '/no-access';
};

export const isSuperAdmin = (checkIsNetworkOwnerOnly?: boolean) => {
  const stringifyUserType = localStorage.getItem('userType');
  const userType =
    stringifyUserType && stringifyUserType !== 'undefined'
      ? JSON.parse(stringifyUserType)
      : null;
  if (checkIsNetworkOwnerOnly) {
    return userType?.name == 'network_owner';
  }
  return userType?.name == 'super_admin' || userType?.name == 'network_owner';
};

// True only for super_admin (isSuperAdmin() without args also matches network_owner)
export const isSuperAdminOnly = () => {
  const stringifyUserType = localStorage.getItem('userType');
  const userType =
    stringifyUserType && stringifyUserType !== 'undefined'
      ? JSON.parse(stringifyUserType)
      : null;
  return userType?.name == 'super_admin';
};

export const isNetworkOwnerOnBoardingCompleted = () => {
  const stringifyUser = localStorage.getItem('user');
  const user =
    stringifyUser && stringifyUser !== 'undefined'
      ? JSON.parse(stringifyUser)
      : null;
  const userType = user?.userType || null;

  return userType?.name == 'network_owner' && user?.completedOnboarding;
};

export const getDateInUTC = (localDate: Date) => {
  return new Date(
    Date.UTC(
      localDate.getFullYear(),
      localDate.getMonth(),
      localDate.getDate(),
      0,
      0,
      0,
      0,
    ),
  );
};

export const getCorrectPath = (fullPath?: string | null, type?: string) => {
  if (!fullPath?.toString()) return '';

  const isRemoteUrl =
    fullPath.toString().includes('http://') ||
    fullPath.toString().includes('https://');

  if (isRemoteUrl) return fullPath;

  const isRemotePath = fullPath.toString().includes('/public/');
  if (isRemotePath) {
    return (
      ENDPOINTS.BASE_URL +
      `public/${
        type == 'image' ? 'images' : type == 'video' ? 'videos' : 'files'
      }/` +
      fullPath.toString().split('/').pop()
    );
  }

  return fullPath;
};

export const getTodayTomorrowYesterday = () => {
  const today = getDateInUTC(new Date());
  const yesterday = getDateInUTC(new Date());
  yesterday.setUTCDate(yesterday.getUTCDate() - 1);
  const tomorrow = getDateInUTC(new Date());
  tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
  return { yesterday, today, tomorrow };
};

export const convertToServerLocalTime = (utcDateString: string) => {
  const utcDateTime = moment(utcDateString);
  const localDateTime = utcDateTime.local();
  const date = new Date();
  date.setUTCFullYear(localDateTime.year());
  date.setUTCMonth(localDateTime.month());
  date.setUTCDate(localDateTime.date());
  date.setUTCHours(localDateTime.hours());
  date.setUTCMinutes(localDateTime.minute());
  date.setUTCSeconds(localDateTime.second());
  date.setUTCMilliseconds(localDateTime.millisecond());
  return date;
};

export const getStartAndEndOfDay = (d: string) => {
  const date = new Date(d);
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();
  // Set start of day to midnight in UTC
  const startOfDay = new Date(Date.UTC(year, month, day, 0, 0, 1));
  // Set end of day to one millisecond before midnight in UTC
  const endOfDay = new Date(Date.UTC(year, month, day, 23, 59, 59, 999));
  return { startOfDay, endOfDay };
};

export async function requestPermissionAndGetToken(prompt = true) {
  try {
    if (!('Notification' in window) || !messaging) {
      console.warn('Push notifications are not supported in this browser.');
      return null;
    }

    let permission = Notification.permission;

    if (permission === 'default' && prompt) {
      // Wrap in a real Promise so older Safari's callback-based API
      // and Edge's potentially-hanging prompt are both handled safely.
      const permissionPromise = new Promise<NotificationPermission>(
        (resolve) => {
          const result = Notification.requestPermission((cbPermission) => {
            resolve(cbPermission);
          });
          // Modern browsers return a Promise; old Safari returns undefined.
          if (result instanceof Promise) {
            result.then(resolve);
          }
        },
      );

      permission = await Promise.race([
        permissionPromise,
        new Promise<NotificationPermission>((resolve) =>
          setTimeout(() => resolve('default'), 10_000),
        ),
      ]);
    }

    if (permission === 'granted') {
      const token = await getToken(messaging, {
        vapidKey: process.env.REACT_APP_VAPID_KEY,
      });
      return token;
    }

    return null;
  } catch (error) {
    console.warn('Failed to get notification token:', error);
    return null;
  }
}

export function generateRenewalsData(
  lastRenewalDate: string,
  renewUntil: string,
  renewalIntervalValue: number,
  renewalIntervalUnit: unitOfTime.DurationConstructor, // ← not string
): RenewalRow[] {
  const renewals: RenewalRow[] = [];

  if (
    !lastRenewalDate ||
    !renewUntil ||
    !renewalIntervalValue ||
    renewalIntervalValue <= 0
  ) {
    return renewals;
  }

  let lastRenewal = moment(lastRenewalDate);
  const endDate = moment(renewUntil);

  // bail if dates are invalid
  if (!lastRenewal.isValid() || !endDate.isValid()) return renewals;

  while (true) {
    // moment(x) clones when x is a Moment, so this won't mutate lastRenewal
    const nextRenewal = moment(lastRenewal).add(
      renewalIntervalValue,
      renewalIntervalUnit,
    );

    if (nextRenewal.isAfter(endDate)) break;

    renewals.push({
      renewalDate: nextRenewal.format('YYYY-MM-DD'),
      reminderBeforeDays: '',
    });

    lastRenewal = nextRenewal;

    if (renewals.length >= 1000) break; // safety limit
  }

  return renewals;
}

export const stringToColor = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = '#';
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    color += ('00' + value.toString(16)).slice(-2);
  }
  return color;
};

export const getContrastColor = (hex: string) => {
  hex = hex.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128 ? '#000' : '#fff';
};

const colorCache: Record<string, string> = {};
let colorIndex = 0;

export const getCircleColor = (id: string) => {
  if (!id) return '#ccc';
  if (colorCache[id]) return colorCache[id];
  let color;
  if (colorIndex < ADDITIONAL_COLORS.length) {
    color = ADDITIONAL_COLORS[colorIndex];
    colorIndex++;
  } else {
    color = stringToColor(id);
  }
  colorCache[id] = color;
  return color;
};
export function debounce<F extends (...args: any[]) => void>(
  func: F,
  delay: number,
) {
  let timer: NodeJS.Timeout;
  return (...args: Parameters<F>) => {
    clearTimeout(timer);
    timer = setTimeout(() => func(...args), delay);
  };
}

export type Event = {
  id: string;
  title: string;
  date: string; // 'YYYY/MM/DD'
  start?: string; // 'HH:mm'
  end?: string; // 'HH:mm'
  source?: any;
};

const fmtDate = (d: string | Date) => moment(d).format('YYYY/MM/DD');
const isSameDay = (a: string | Date, b: string | Date) =>
  moment(a).isSame(b, 'day');

const toEvent = (
  ann: any,
  date: Date,
  start?: string,
  end?: string,
  source?: any,
): Event => ({
  id: ann._id,
  title: ann.announcementTitle,
  date: fmtDate(date),
  start,
  end,
  source: source ?? ann,
});

// Returns whether the selected day has an occurrence, plus suggested start/end
export function classifyAnnouncementForDate(
  ann: any,
  selectedDate: Date,
  mode: 'daily' | 'scheduled',
): { bucket: 'single' | 'recurring' | 'allDay' | null; event?: Event } {
  const sched = ann?.scheduling;

  // No scheduling => treat as all-day
  if (!sched?.schedulingType) {
    return { bucket: 'allDay', event: toEvent(ann, selectedDate) };
  }

  const type = sched.schedulingType as
    | 'default'
    | 'all_day'
    | 'by_day_and_time'
    | 'between_days_and_hours'
    | 'daily_at_fixed_time'
    | 'custom';

  // DEFAULT / ALL_DAY => allDay
  if (type === 'default' || type === 'all_day') {
    return { bucket: 'allDay', event: toEvent(ann, selectedDate) };
  }

  // Everything else must have startTime/endTime
  const start = sched.startTime;
  const end = sched.endTime;
  if (!start || !end) return { bucket: null };

  // BETWEEN_DAYS_AND_HOURS:
  // - single if startDate & endDate are same day
  // - otherwise recurring (but only relevant in scheduled mode)
  if (type === 'between_days_and_hours') {
    const isSingleDayRange =
      sched.startDate &&
      sched.endDate &&
      isSameDay(sched.startDate, sched.endDate);

    const bucket =
      mode === 'daily'
        ? 'single' // daily view needs it in the timeline
        : isSingleDayRange
          ? 'single'
          : 'recurring';

    return { bucket, event: toEvent(ann, selectedDate, start, end) };
  }

  // BY_DAY_AND_TIME
  // show in timeline (single)
  if (type === 'by_day_and_time') {
    const bucket = 'single';
    return { bucket, event: toEvent(ann, selectedDate, start, end) };
  }

  // DAILY_AT_FIXED_TIME / CUSTOM:
  // - daily view -> show in timeline (single)
  // - scheduled view -> show in recurring section
  if (type === 'daily_at_fixed_time' || type === 'custom') {
    const bucket = mode === 'daily' ? 'single' : 'recurring';
    return { bucket, event: toEvent(ann, selectedDate, start, end) };
  }

  return { bucket: null };
}

export const getAllPermissions = (data: any) => {
  const result: any = [];

  function traverse(node: any) {
    if (!node) return;

    // Add this group's permissions
    if (node.permissions && Array.isArray(node.permissions)) {
      result.push(...node.permissions);
    }

    // Traverse children recursively
    if (node.children && Array.isArray(node.children)) {
      node.children.forEach((child: any) => traverse(child));
    }
  }

  // Data may be array of groups
  data.forEach((group: any) => traverse(group));

  return result;
};

export const isMissingAny = (str: string) => {
  const hasLetter = /[A-Za-z]/.test(str);
  const hasNumber = /[0-9]/.test(str);
  const hasSpecial = /[^A-Za-z0-9]/.test(str);

  return !(hasLetter && hasNumber && hasSpecial);
};

export const formatNumber = (input: string): string => {
  // Remove non-digits
  let digits = input?.replace(/\D/g, '');

  // Ensure it starts with 972
  if (!digits.startsWith('972')) {
    return 'Invalid number';
  }

  // Remove country code
  digits = digits.slice(3);

  // Detect prefix
  // Known area codes (landline + mobile)
  const prefixes = [
    '2',
    '3',
    '4',
    '8',
    '9',
    '50',
    '51',
    '52',
    '53',
    '54',
    '55',
    '56',
    '57',
    '58',
    '59',
    '72',
    '73',
    '74',
    '75',
    '76',
    '77',
  ];

  let prefix = null;

  // Find the longest matching prefix
  for (let p of prefixes.sort((a, b) => b.length - a.length)) {
    if (digits.startsWith(p)) {
      prefix = p;
      break;
    }
  }

  if (!prefix) {
    prefix = digits.slice(0, 2);
  }

  let rest = digits.slice(prefix.length);

  let firstPart = rest.slice(0, rest.length - 4);
  let lastPart = rest.slice(-4);

  return `+972 (0${prefix}) ${firstPart}-${lastPart}`;
};

export const sortByDate = (a: any, b: any) => {
  const dateA = new Date(a.updatedAt || a.createdAt).getTime();
  const dateB = new Date(b.updatedAt || b.createdAt).getTime();
  return dateB - dateA; // newest first
};

export const DAYS = [
  { label: `א'`, value: 'sunday', index: 0 },
  { label: `ב'`, value: 'monday', index: 1 },
  { label: `ג'`, value: 'tuesday', index: 2 },
  { label: `ד'`, value: 'wednesday', index: 3 },
  { label: `ה'`, value: 'thursday', index: 4 },
  { label: `ו'`, value: 'friday', index: 5 },
  { label: `ש'`, value: 'saturday', index: 6 },
];

export const announcementSidebarFilters = (
  apiAnnouncementsData: Record<string, any>,
  announcementSearchText: string,
) => {
  // 1️⃣ Flatten & dedupe announcements
  const allAnnouncements = apiAnnouncementsData.data.reduce(
    (acc: any[], department: any) => {
      (department.announcements || []).forEach((announcement: any) => {
        if (!acc.some((a) => a._id === announcement._id)) {
          acc.push(announcement);
        }
      });
      return acc;
    },
    [],
  );

  // 2️⃣ Search filter
  const filteredBySearch = allAnnouncements.filter((announcement: any) =>
    announcementSearchText
      ? announcement.announcementTitle
          ?.toLowerCase()
          .includes(announcementSearchText.toLowerCase()) ||
        announcement.description
          ?.toLowerCase()
          .includes(announcementSearchText.toLowerCase())
      : true,
  );

  const departmentCounts: Record<string, number> = {};

  // 3️⃣ ALL = UNIQUE announcements only ⭐⭐⭐
  departmentCounts['all'] = filteredBySearch.length;

  // 4️⃣ Per-department counts (matches main logic)
  filteredBySearch.forEach((announcement: any) => {
    const { departments, originalDepartments } = announcement;

    // departments === 'all' → appears in every department
    if (departments === 'all') {
      apiAnnouncementsData.data.forEach((dept: any) => {
        if (dept.departmentId !== 'all') {
          departmentCounts[dept.departmentId] =
            (departmentCounts[dept.departmentId] || 0) + 1;
        }
      });
      return;
    }

    // Multiple departments
    if (Array.isArray(originalDepartments) && originalDepartments.length > 0) {
      originalDepartments.forEach((deptId: string) => {
        departmentCounts[deptId] = (departmentCounts[deptId] || 0) + 1;
      });
      return;
    }

    // Fallback: find owning department
    // const owningDept = apiAnnouncementsData.data.find((dept: any) =>
    //   dept.announcements?.some((a: any) => a._id === announcement._id),
    // );

    // if (owningDept) {
    //   departmentCounts[owningDept.departmentId] =
    //     (departmentCounts[owningDept.departmentId] || 0) + 1;
    // }
  });

  // 5️⃣ Build sidebar filters
  const filters = apiAnnouncementsData.data.map((department: any) => {
    const deptId = department.departmentId;
    return {
      type: deptId,
      text: department.departmentName,
      icon:
        deptId === 'all'
          ? SVG.filterSidebarAllIcon
          : getCorrectPath(department.departmentIcon, 'image'),
      count: departmentCounts[deptId] || 0,
    };
  });

  // Ensure "all" first
  return filters.sort((a: FilterItem, b: FilterItem) => {
    if (a.type === 'all') return -1;
    if (b.type === 'all') return 1;
    return 0;
  });
};

export const isNowInShift = (start: string, end: string, now: string) => {
  // strings are "HH:mm" so lexicographic compare works
  if (start <= end) {
    // normal same-day shift
    return now >= start && now <= end;
  }
  // overnight shift (e.g. 22:00 -> 06:00)
  return now >= start || now <= end;
};

export const logoutHelper = () => {
  localStorage.removeItem('userToken');
  localStorage.removeItem('user');
  localStorage.removeItem('userType');
  localStorage.removeItem('userName');
  localStorage.removeItem('userId');
  localStorage.removeItem('loggedIn');
  localStorage.removeItem('selectedLocation');
  localStorage.removeItem('defaultReservationDepartments');

  clearReactQueryCache();
};

/**
 * Safely get the display name from a roleType field.
 * roleType can be either an object (with name.he / name.en) or a plain string.
 */
export const getRoleTypeName = (
  roleType: { name?: { he?: string; en?: string } } | string | undefined | null,
  lang: 'he' | 'en' = 'he',
): string => {
  if (!roleType) return '';
  if (typeof roleType === 'string') return roleType;
  return roleType?.name?.[lang] || '';
};

/**
 * Safely get the icon from a roleType field.
 * roleType can be either an object (with icon) or a plain string.
 */
export const getRoleTypeIcon = (
  roleType: { icon?: string } | string | undefined | null,
): string | undefined => {
  if (!roleType || typeof roleType === 'string') return undefined;
  return roleType?.icon;
};

export const buildPublicFormPayload = (
  questions: FormQuestion[],
  answers: Record<string, any>,
) => {
  return {
    answers: questions
      .filter((question) => {
        const v = answers[question._id];
        if (v === undefined || v === null) return false;
        if (
          question.type === 'file-upload' &&
          Array.isArray(v) &&
          v.length === 0
        ) {
          return false;
        }
        return true;
      })
      .map((question) => {
        const value = answers[question._id];

        switch (question.type) {
          case 'checkbox':
            const optionsWithoutOther = value?.selected?.filter(
              (v: string) => v !== 'other',
            );
            const filteredOptions = question?.options?.filter((op) =>
              optionsWithoutOther?.includes(op?._id),
            );
            const otherOption = question?.options?.find((op) => op?.isOther);
            const payload = [
              ...filteredOptions?.map((op) => ({
                isOther: op.isOther,
                label: op.label,
                image: op.image,
              })),
              ...[
                value?.otherText &&
                  value?.otherText !== '' && {
                    isOther: true,
                    input: value?.otherText,
                    image: otherOption?.image,
                  },
              ],
            ].filter(Boolean);
            return {
              questionId: question._id,
              value: payload ?? [],
            };

          case 'multiple-choice':
            let multiChoicePayload;
            if (value?.selected === 'other') {
              const otherOption = question?.options?.find((op) => op?.isOther);
              multiChoicePayload = {
                input: value?.otherText,
                isOther: true,
                image: otherOption?.image,
              };
            } else {
              const selectedOption = question?.options?.find(
                (op) => op?._id === value?.selected,
              );
              multiChoicePayload = {
                label: selectedOption?.label,
                isOther: selectedOption?.isOther,
                image: selectedOption?.image,
              };
            }

            return {
              questionId: question._id,
              value: multiChoicePayload ?? {},
            };

          case 'dropdown':
            const selectedDropdownOption = question?.options?.find(
              (op) => (op?._id ?? op?.key) === value,
            );
            return {
              questionId: question._id,
              value: selectedDropdownOption
                ? {
                    label: selectedDropdownOption.label,
                    isOther: false,
                  }
                : {},
            };

          case 'short-answer':
            return {
              questionId: question._id,
              value: value ?? '',
            };

          case 'linear-scale':
          case 'rating':
            return {
              questionId: question._id,
              value: value?.toString() ?? null,
            };

          case 'date':
            let datePayload;
            if (typeof value == 'string') {
              datePayload = { startDate: value };
            } else {
              datePayload = { startDate: value?.from, endDate: value?.to };
            }
            return {
              questionId: question._id,
              value: datePayload ?? {},
            };
          case 'time':
            let timePayload;
            if ('hours' in value && !('from' in value)) {
              const sh = (value?.hours || '0').padStart(2, '0');
              const sm = (value?.minutes || '0').padStart(2, '0');
              timePayload = {
                startTime: `${sh}:${sm}`,
              };
            } else {
              const fh = (value?.from?.hours || '0').padStart(2, '0');
              const fm = (value?.from?.minutes || '0').padStart(2, '0');
              const th = (value?.to?.hours || '0').padStart(2, '0');
              const tm = (value?.to?.minutes || '0').padStart(2, '0');
              timePayload = {
                startTime: `${fh}:${fm}`,
                endTime: `${th}:${tm}`,
              };
            }
            return {
              questionId: question._id,
              value: timePayload ?? {},
            };

          case 'file-upload':
            return {
              questionId: question._id,
              value: Array.isArray(value) ? value : [],
            };

          case 'range': {
            // Dual answer (form: range) → { from, to, image? }
            // Single answer (between/from-to/textual) → { value, image? }
            const rangePayload: Record<string, any> =
              'from' in value
                ? { from: value.from, to: value.to }
                : { value: value.value };
            if (value.image) {
              rangePayload.image = value.image;
            }
            return {
              questionId: question._id,
              value: rangePayload,
            };
          }

          default:
            return {
              questionId: question._id,
              value: value ?? null,
            };
        }
      }),
  };
};

export const isAdmin = () => {
  const stringifyUser = localStorage.getItem('user');
  const user =
    stringifyUser && stringifyUser !== 'undefined'
      ? JSON.parse(stringifyUser)
      : null;
  const userRoleType = user?.roles?.[0]?.roleType;

  if (userRoleType && userRoleType?._id === '6916f865d7aba51bcb7daa6c') {
    return true;
  }

  return false;
};

/**
 * Returns the `department` query parameter to scope API results by the
 * logged-in user's department(s). Returns an empty object for super-admins,
 * admins, and users whose departmentId is "all" so no filter is applied.
 */
export const getDepartmentFilterParams = (): {
  department?: string | string[];
} => {
  if (isSuperAdmin() || isAdmin()) return {};

  const raw = localStorage.getItem('user');
  const user = raw && raw !== 'undefined' ? JSON.parse(raw) : null;

  if (!user?.departmentId || user.departmentId === 'all') return {};

  return {
    department: Array.isArray(user.departmentId)
      ? user.departmentId.map((d: any) => d._id)
      : user.departmentId?._id,
  };
};

export const isCreatedByNonAdmin = (user: Partial<UserItem>) => {
  if (
    user?.roles?.length !== 0 &&
    typeof user?.roles?.[0]?.roleType == 'object' &&
    user?.roles?.[0]?.roleType?._id !== '6916f865d7aba51bcb7daa6c'
  ) {
    return true;
  }
  return false;
};

export const getReservationInfo = (source: any) => {
  const time = `${source?.scheduling?.startTime} - ${source?.scheduling?.endTime}`;
  const zone =
    source?.reservation_zone && typeof source.reservation_zone === 'object'
      ? source.reservation_zone
      : null;
  const label = [
    source?.number_of_people != null ? `${source.number_of_people}P` : null,
    zone?.title?.length > 10
      ? `${zone.title.slice(0, 10)}...`
      : zone?.title || null,
    source?.client_name
      ? `- ${source.client_name?.length > 8 ? `${source.client_name.slice(0, 8)}...` : source.client_name}`
      : null,
  ]
    .filter(Boolean)
    .join(' ');
  return { zoneColor: zone?.color as string | undefined, label, time };
};

export const getAnnouncementIcon = (
  announcementType: string,
  isUrgent: boolean,
  groupId?: string,
) => {
  const isRegular = announcementType == 'Regular';
  const isAlert = announcementType == 'Error';
  const isTraining = announcementType == 'Training';
  const isAdministration = announcementType == 'Administration';
  const isRegulation = announcementType == 'Regulation';
  const isNotice = announcementType == 'Notice';
  const isQuick = announcementType == 'Quick';
  const isReservation = announcementType == 'Reservation';
  const isMenu = announcementType == 'Menu';

  if (groupId === 'messages') {
    return isUrgent
      ? SVG.regularAnnouncementRedIcon
      : SVG.regularAnnouncementOutlineIcon;
  } else if (isAlert) {
    return SVG.redTriangleIcon;
  } else if (isQuick) {
    return SVG.quickTypeIcon;
  } else if (isReservation) {
    return SVG.tableIcon;
  } else if (isMenu) {
    return SVG.menuTypeIcon;
  } else if (isRegular) {
    return isUrgent
      ? SVG.regularAnnouncementRedIcon
      : SVG.regularAnnouncementIcon;
  } else if (isTraining) {
    return isUrgent ? SVG.tutorialsActiveRedIcon : SVG.tutorialsActiveIcon;
  } else if (isAdministration) {
    return isUrgent ? SVG.settingRedIcon : SVG.settingBlackIcon;
  } else if (isRegulation) {
    return isUrgent ? SVG.administrationRedIcon : SVG.administrationSmallIcon;
  } else if (isNotice) {
    return isUrgent ? SVG.exclamationCircleIcon : SVG.exclamationCircleIcon;
  }
};

export const getActiveSectionText = (section: string) => {
  if (section?.includes('subBranches')) return 'סניף משנה';
  else if (section?.includes('branches')) return 'סניף';
  else if (section?.includes('network')) return 'רשת';
  return '';
};

export const isReservationAnnouncement = (announcement: any): boolean => {
  const isReservation = announcement?.announcementType == 'Reservation';
  const hasReservationDetails =
    announcement?.reservation_zone?.title ||
    announcement?.number_of_people ||
    announcement?.client_name ||
    announcement?.phone;

  return isReservation && hasReservationDetails;
};

// onboarding helpers
export const findParentBranchId = (
  network: NetworkNodeFormValues[],
  subBranchId: string,
): string | null => {
  for (const client of network) {
    for (const branch of client.branches) {
      if ((branch.subBranches ?? []).some((sb) => sb.id === subBranchId)) {
        return branch.id;
      }
    }
  }
  return null;
};

export function getInitials(text: string): string {
  if (!text.trim()) return '';

  const words = text.trim().split(/\s+/).filter(Boolean);

  const firstChar = (str: string): string => {
    if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
      const segmenter = new Intl.Segmenter(undefined, {
        granularity: 'grapheme',
      });

      const iterator = segmenter.segment(str)[Symbol.iterator]();
      const result = iterator.next();

      return result.done ? '' : result.value.segment;
    }

    // Fallback
    return Array.from(str)[0] ?? '';
  };

  let initials: string;

  if (words.length >= 2) {
    initials = firstChar(words[0]) + firstChar(words[1]);
  } else {
    // Single word: take first two graphemes
    // initials = Array.from(words[0]).slice(0, 2).join("");
    initials = '';
  }

  return initials.toLocaleUpperCase();
}

export const getLocationRoles = (
  clients: ClientOption[],
  clientIds: string[],
  branchIds?: string[],
  subBranchIds?: string[],
) => {
  const branchSet = new Set(branchIds ?? []);
  const subBranchSet = new Set(subBranchIds ?? []);
  const map = new Map<string, RoleFormValues>();
  clients
    .filter((c) => clientIds.includes(c.id))
    .forEach((c) => {
      if (branchSet.size === 0) {
        c.roles?.forEach((r) => map.set(r.id, r));
        c.branches.forEach((b) => {
          b.roles?.forEach((r) => map.set(r.id, r));
          b.subBranches.forEach((sb) =>
            sb.roles?.forEach((r) => map.set(r.id, r)),
          );
        });
      } else {
        c.branches.forEach((b) => {
          if (!branchSet.has(b.id)) return;
          const selectedSubs = b.subBranches.filter((sb) =>
            subBranchSet.has(sb.id),
          );
          if (selectedSubs.length > 0) {
            selectedSubs.forEach((sb) =>
              sb.roles?.forEach((r) => map.set(r.id, r)),
            );
          } else {
            b.roles?.forEach((r) => map.set(r.id, r));
            b.subBranches.forEach((sb) =>
              sb.roles?.forEach((r) => map.set(r.id, r)),
            );
          }
        });
      }
    });
  return Array.from(map.values());
};
export const getLocationDepartments = (
  clients: ClientOption[],
  clientIds: string[],
  branchIds?: string[],
  subBranchIds?: string[],
) => {
  const branchSet = new Set(branchIds ?? []);
  const subBranchSet = new Set(subBranchIds ?? []);
  const map = new Map<string, DepartmentFormValues>();
  clients
    .filter((c) => clientIds.includes(c.id))
    .forEach((c) => {
      if (branchSet.size === 0) {
        // No branch selected → all departments in the client tree
        c.departments.forEach((d) => map.set(d.id, d));
        c.branches.forEach((b) => {
          b.departments.forEach((d) => map.set(d.id, d));
          b.subBranches.forEach((sb) =>
            sb.departments.forEach((d) => map.set(d.id, d)),
          );
        });
      } else {
        // Branch(es) selected → no client-level departments.
        // If a selected branch has selected subBranches, show only those subBranches' departments.
        // Otherwise show that branch's departments + all its subBranches' departments.
        c.branches.forEach((b) => {
          if (!branchSet.has(b.id)) return;
          const selectedSubs = b.subBranches.filter((sb) =>
            subBranchSet.has(sb.id),
          );
          if (selectedSubs.length > 0) {
            selectedSubs.forEach((sb) =>
              sb.departments.forEach((d) => map.set(d.id, d)),
            );
          } else {
            b.departments.forEach((d) => map.set(d.id, d));
            b.subBranches.forEach((sb) =>
              sb.departments.forEach((d) => map.set(d.id, d)),
            );
          }
        });
      }
    });
  return Array.from(map.values());
};

export function getLocationNames(
  clients: ClientOption[],
  clientIds: string[] = [],
  branchIds: string[] = [],
  subBranchIds: string[] = [],
  singleArrayMode: boolean = false,
) {
  const clientMap = new Map<string, string>();
  const branchMap = new Map<string, string>();
  const subBranchMap = new Map<string, string>();

  for (const client of clients) {
    clientMap.set(client.id, client.name);

    for (const branch of client.branches ?? []) {
      branchMap.set(branch.id, branch.name);

      for (const subBranch of branch.subBranches ?? []) {
        subBranchMap.set(subBranch.id, subBranch.name);
      }
    }
  }
  if (singleArrayMode) {
    const allIds = [...clientIds, ...branchIds, ...subBranchIds];
    return allIds
      .map(
        (id) =>
          clientMap.get(id) ??
          branchMap.get(id) ??
          subBranchMap.get(id) ??
          null,
      )
      .filter(Boolean);
  }

  return {
    clients: clientIds.map((id) => ({
      id,
      name: clientMap.get(id) ?? null,
    })),
    branches: branchIds.map((id) => ({
      id,
      name: branchMap.get(id) ?? null,
    })),
    subBranches: subBranchIds.map((id) => ({
      id,
      name: subBranchMap.get(id) ?? null,
    })),
  };
}
